import { SOCIAL_AD_TYPES, SOCIAL_FORMATS } from "utils/constants";
import { isURL, isProtocolOptionalURL, maxLength, requiredNotNull } from "widgets/forms/validators/validators";
import { dimensions, filesize, duration } from "widgets/forms/validators/Assets";
import { MEGA, SECONDS_PER_MIN } from "utils/common";
import { creativeNameAllowedChars, creativeNameMaxLength } from "widgets/forms/validators/Creatives";
import {
	conditionalRequiredNotNull,
	errorMessageReducer,
	requiredAPIOption,
	requiredAsset,
	makeDynamicConfig,
	getCardWarnings,
	minCards,
	minimumCards,
	cardValidator,
	validateUnlessAdStudio,
	conditionalValidator
} from "social/validators/validators";
import {
	mergeLists,
	uniqueVariableName,
	uniquePACVariableName,
	uniqueCardVariableName
} from "social/validators/utils";

export function requireActivePlacement(selectedPlacements, allValues) {
	if (
		!allValues.placementAssetCustomizationType
		|| allValues.placementAssetCustomizationType === "none"
		|| selectedPlacements?.length
	) { return; }

	const { placements = {} } = allValues;

	for (const placement of Object.values(placements)) {
		if (placement.status === "active") {
			// As soon as we find an active placement we can exit
			return;
		}
	}

	// If we get here, we have no active placements
	return "At least one is required in addition to the default";
}

export function placementErrorMessageReducer(validators, values, allValues = {}) {
	/**
	 * This function is nearly the same as the base message reducer. However, we return the errors exactly,
	 * instead of a keyed object.
	 *
	 * `values` is the values of the current placement
	 * `allValues` is the values of the entire creative (which is used for the default placement)
	 */
	let hasErrors = false;
	const errors = Object.keys(validators).reduce((prev, key) => {
		let message = "";

		for (const validator of validators[key]) {
			message = validator(values[key], allValues, {}, key);
			if (message) {
				hasErrors = true;
				return {
					...prev,
					[key]: message,
				};
			}
		}

		return prev;
	}, {});

	if (hasErrors) {
		return errors;
	}
}

export function placementsWarningHandler(fieldValidators) {
	/**
	 * Uses the fieldValidators from an `adType` to compile and attach placement warnings
	 */
	return (allPlacementValues, allValues) => {
		const placements = Object.keys(allPlacementValues || {});
		let hasError = false;
		const warnings = placements.reduce((prev, placementType) => {
			const placementValues = allPlacementValues[placementType];
			if (placementValues.status !== "active") {
				return prev;
			}

			const warnings = placementErrorMessageReducer(fieldValidators, placementValues, allValues);

			if (warnings) {
				hasError = true;
				return {
					...prev,
					[placementType]: warnings,
				};
			}

			return prev;
		}, {});

		if (hasError) {
			return warnings;
		}
	};
}

export const FACEBOOK_IMAGE_CONSTRAINTS = {
	MAX_IMAGE_MB: 10,
	MAX_LENGTH_LANDING_PAGE: 800,
	DIMENSIONS: { minWidth: 254, minHeight: 133 },
	IMAGE_ACCEPT: ".jpg, .jpeg, .png, .gif",
};

export function facebookImageWarning(allValues, props) {
	const {
		MAX_IMAGE_MB,
		DIMENSIONS,
		MAX_LENGTH_LANDING_PAGE,
	} = FACEBOOK_IMAGE_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE;
	const values = allValues[formName] || {};

	const fieldValidators = {
		image: [requiredAsset, filesize({ max: MAX_IMAGE_MB * MEGA }), dimensions(DIMENSIONS)],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
		displayLink: [isProtocolOptionalURL],
		facebookPage: [requiredAPIOption],
		"_select-aspect-ratios": [requireActivePlacement],
	};
	/*
		To keep validation consistent and to avoid duplication, we pass along fieldValidators of relevant fields to the placements validator
	*/
	const placementsWarnings = {
		image: fieldValidators.image,
		displayLink: fieldValidators.displayLink,
		landingPage: fieldValidators.landingPage,
	};
	fieldValidators.placements = [placementsWarningHandler(placementsWarnings)];

	return errorMessageReducer(fieldValidators, values, formName);
}

export const FB_MO_IMAGE_VALIDATORS = {
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(FACEBOOK_IMAGE_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE)
	],
	cta: [
		requiredNotNull
	],
	displayLink: [
		isProtocolOptionalURL
	],
	image: validateUnlessAdStudio([
		requiredAsset,
		filesize({
			max: FACEBOOK_IMAGE_CONSTRAINTS.MAX_IMAGE_MB * MEGA,
		}),
		dimensions(FACEBOOK_IMAGE_CONSTRAINTS.DIMENSIONS)
	]),
};

const FACEBOOK_IMAGE_MO_WARNINGS = mergeLists(
	{
		image: [uniquePACVariableName],
		headline: [uniquePACVariableName],
		primaryText: [uniquePACVariableName],
		description: [uniquePACVariableName],
		landingPage: [uniquePACVariableName],
		displayLink: [uniquePACVariableName],
		cta: [uniquePACVariableName],

		placements: [
			placementsWarningHandler({
				image: [uniquePACVariableName],
				headline: [uniquePACVariableName],
				primaryText: [uniquePACVariableName],
				landingPage: [uniquePACVariableName],
				displayLink: [uniquePACVariableName],
			})
		],
	},
	makeDynamicConfig({
		...FB_MO_IMAGE_VALIDATORS,
		"_select-aspect-ratios": [requireActivePlacement],
		placements: [
			placementsWarningHandler(makeDynamicConfig({
				image: FB_MO_IMAGE_VALIDATORS.image,
				displayLink: FB_MO_IMAGE_VALIDATORS.displayLink,
				landingPage: FB_MO_IMAGE_VALIDATORS.landingPage,
			})),
		],
	})
);

export function facebookImageMOWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(FACEBOOK_IMAGE_MO_WARNINGS, values, formName);
	}
}

export function facebookImageValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}

export function facebookImageMOValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...FACEBOOK_IMAGE_MO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

export const FACEBOOK_VIDEO_CONSTRAINTS = {
	MAX_LENGTH_LANDING_PAGE: 800,
	MAX_VIDEO_MB: 512,
	DIMENSIONS: { minWidth: 120, minHeight: 120 },
	MIN_VIDEO_SEC: 1,
	MAX_VIDEO_MIN: 15,
	VIDEO_ACCEPT: ".mp4, .mov",
	THUMBNAIL_ACCEPT: ".jpg, .jpeg, .png, .gif",
	MAX_THUMBNAIL_MB: 10,
	THUMBNAIL_DIMENSIONS: { minWidth: 254, minHeight: 133 },
};

export function facebookVideoWarning(allValues, props) {
	const {
		MAX_LENGTH_LANDING_PAGE,
		MAX_VIDEO_MB,
		DIMENSIONS,
		MIN_VIDEO_SEC,
		MAX_VIDEO_MIN,
	} = FACEBOOK_VIDEO_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO;
	const values = allValues[formName] || {};

	const fieldValidators = {
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
		displayLink: [isProtocolOptionalURL],
		video: [
			requiredAsset,
			filesize({ max: MAX_VIDEO_MB * MEGA }),
			dimensions(DIMENSIONS),
			duration({ min: MIN_VIDEO_SEC, max: MAX_VIDEO_MIN * SECONDS_PER_MIN }),
		],
		facebookPage: [requiredAPIOption],
		"_select-aspect-ratios": [requireActivePlacement],
	};
	const placementsWarnings = {
		video: fieldValidators.video,
		displayLink: fieldValidators.displayLink,
		landingPage: fieldValidators.landingPage,
	};
	fieldValidators.placements = [placementsWarningHandler(placementsWarnings)];

	return errorMessageReducer(fieldValidators, values, formName);
}

export const FB_MO_VIDEO_VALIDATORS = {
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	cta: [
		requiredNotNull,
	],
	displayLink: [
		isProtocolOptionalURL,
	],
	video: validateUnlessAdStudio([
		requiredAsset,
		filesize({
			max: FACEBOOK_VIDEO_CONSTRAINTS.MAX_VIDEO_MB * MEGA,
		}),
		dimensions(FACEBOOK_VIDEO_CONSTRAINTS.DIMENSIONS),
		duration({
			min: FACEBOOK_VIDEO_CONSTRAINTS.MIN_VIDEO_SEC,
			max: FACEBOOK_VIDEO_CONSTRAINTS.MAX_VIDEO_MIN * SECONDS_PER_MIN,
		}),
	]),
	thumbnail: validateUnlessAdStudio([
		filesize({
			max: FACEBOOK_VIDEO_CONSTRAINTS.MAX_THUMBNAIL_MB * MEGA,
		}),
		dimensions(FACEBOOK_VIDEO_CONSTRAINTS.THUMBNAIL_DIMENSIONS),
	]),
};


let FACEBOOK_VIDEO_MO_WARNINGS = mergeLists(
	{
		video: [uniquePACVariableName],
		thumbnail: [uniquePACVariableName],
		headline: [uniquePACVariableName],
		primaryText: [uniquePACVariableName],
		description: [uniquePACVariableName],
		landingPage: [uniquePACVariableName],
		displayLink: [uniquePACVariableName],
		cta: [uniquePACVariableName],

		placements: [
			placementsWarningHandler({
				video: [uniquePACVariableName],
				thumbnail: [uniquePACVariableName],
				headline: [uniquePACVariableName],
				primaryText: [uniquePACVariableName],
				description: [uniquePACVariableName],
				landingPage: [uniquePACVariableName],
				displayLink: [uniquePACVariableName],
			}),
		],
	},
	makeDynamicConfig({
		...FB_MO_VIDEO_VALIDATORS,
		"_select-aspect-ratios": [requireActivePlacement],
		placements: [
			placementsWarningHandler(makeDynamicConfig({
				video: FB_MO_VIDEO_VALIDATORS.video,
				displayLink: FB_MO_VIDEO_VALIDATORS.displayLink,
				landingPage: FB_MO_VIDEO_VALIDATORS.landingPage,
			})),
		],
	})
);

export function facebookVideoMOWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(FACEBOOK_VIDEO_MO_WARNINGS, values, formName);
	}
}

const requiredWithSeeMoreCard = conditionalRequiredNotNull("seeMoreCard");

function requiredWithImageType(value, allValues, card = {}){
	const { type } = card;
	if(type === SOCIAL_FORMATS.IMAGE){
		return requiredNotNull(value);
	}
}

function requiredWithVideoType(value, allValues, card = {}){
	const { type } = card;
	if(type === SOCIAL_FORMATS.VIDEO){
		return requiredNotNull(value);
	}
}

function requiredWithDynamicImageType(value, allValues, card = {}){
	const { type } = card;
	if(type === SOCIAL_FORMATS.IMAGE){
		return requiredAsset(value, allValues);
	}
}

function requiredWithDynamicVideoType(value, allValues, card = {}){
	const { type } = card;
	if(type === SOCIAL_FORMATS.VIDEO){
		return requiredAsset(value, allValues);
	}
}

export function facebookCarouselWarning(allValues, props) {
	const {
		MAX_LENGTH_LANDING_PAGE,
	} = FACEBOOK_VIDEO_CONSTRAINTS;

	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL;
	const values = allValues[formName] || {};

	const warn = {
		facebookPage: [requiredAPIOption],
		seeMoreDisplayLink: [isProtocolOptionalURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		seeMoreDisplayUrl: [requiredWithSeeMoreCard, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
		cta: [requiredNotNull],
	};

	const mainWarnings = errorMessageReducer(warn, values, formName);

	const { cards = []} = values;
	const cardValidators = {
		image: [requiredWithImageType, requiredWithVideoType],
		video: [requiredWithVideoType],
		landingPage: [requiredNotNull, isURL, maxLength(MAX_LENGTH_LANDING_PAGE)],
	};

	const allWarnings = {
		...mainWarnings?.[formName],
	};

	const cardWarnings = getCardWarnings(cards, values, cardValidators);
	if(cardWarnings.length){
		allWarnings.cards = cardWarnings;
	}

	const cardCount = minimumCards(cards);
	if(cardCount){
		allWarnings.cardCount = cardCount;
	}

	if(Object.keys(allWarnings).length){
		return { [formName]: allWarnings };
	}
}
export const FACEBOOK_CAROUSEL_MO_CARD_VALIDATORS = {
	image: validateUnlessAdStudio([
		requiredWithDynamicImageType,
		requiredWithDynamicVideoType,
	]),
	video: validateUnlessAdStudio([
		requiredWithDynamicVideoType,
	]),
	landingPage: [
		requiredNotNull,
		isURL,
		maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
};

const FACEBOOK_CAROUSEL_MO_CARD_WARNINGS = mergeLists(
	{
		image: [uniqueCardVariableName],
		video: [uniqueCardVariableName],
		headline: [uniqueCardVariableName],
		description: [uniqueCardVariableName],
		landingPage: [uniqueCardVariableName],
	},
	makeDynamicConfig({ ...FACEBOOK_CAROUSEL_MO_CARD_VALIDATORS })
);

export const FACEBOOK_CAROUSEL_MO_VALIDATORS = {
	seeMoreDisplayLink: [
		isProtocolOptionalURL,
		maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE),
	],
	seeMoreDisplayUrl: [
		conditionalValidator("seeMoreCard", requiredNotNull, seeMoreCard => seeMoreCard?.value),
		conditionalValidator("seeMoreCard", isURL, seeMoreCard => seeMoreCard?.value),
		conditionalValidator("seeMoreCard", maxLength(FACEBOOK_VIDEO_CONSTRAINTS.MAX_LENGTH_LANDING_PAGE), seeMoreCard => seeMoreCard?.value),
	],
	cta: [
		requiredNotNull,
	],
	cards: [
		cardValidator(FACEBOOK_CAROUSEL_MO_CARD_WARNINGS),
	],
	cardCount: [
		minCards,
	],
};

const FACEBOOK_CAROUSEL_MO_WARNINGS = mergeLists(
	{
		primaryText: [uniqueVariableName],
		cta: [uniqueVariableName],
		seeMoreDisplayLink: [conditionalValidator("seeMoreCard", uniqueVariableName, seeMoreCard => seeMoreCard?.value)],
		seeMoreDisplayUrl: [conditionalValidator("seeMoreCard", uniqueVariableName, seeMoreCard => seeMoreCard?.value)],
	},
	makeDynamicConfig({
		...FACEBOOK_CAROUSEL_MO_VALIDATORS,
	})
);

export function facebookCarouselMOWarning(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO;
	const values = allValues[formName] || {};

	if(!values.isDynamic){
		return errorMessageReducer(FACEBOOK_CAROUSEL_MO_WARNINGS, values, formName);
	}
}


export function facebookVideoValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}

export function facebookVideoMOValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...FACEBOOK_VIDEO_MO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}

export function facebookCarouselValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL;
	const values = allValues[formName] || {};

	const validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	return errorMessageReducer(validators, values, formName);
}

export function facebookCarouselMOValidator(allValues, props) {
	const formName = SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO;
	const values = allValues[formName] || {};

	let validators = {
		name: [requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars],
	};

	if(values.isDynamic){
		validators = {
			...validators,
			...FACEBOOK_CAROUSEL_MO_WARNINGS,
		};
	}

	return errorMessageReducer(validators, values, formName);
}
